import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, KeyboardEvent, ChangeEvent} from 'react';

import {sendDeepResearchMessage} from 'llm/deep-research/DeepResearchDAO';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ToggleDropdown from 'nav/ToggleDropdown';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BLUE_LIGHT, PURPLE_DARK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ListingHeader from '../header/ListingHeader';

export interface DeepResearchSidepanelProps {
  isExpanded: boolean;
}

export function DeepResearchSidepanel(props: DeepResearchSidepanelProps): JSX.Element {
  const [message, setMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [chatId, _setChatId] = useState<string | undefined>(undefined);

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setMessage(e.target.value);
  };

  const handleKeyDown = async (e: KeyboardEvent<HTMLTextAreaElement>): Promise<void> => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await handleSubmit();
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!message.trim() || isSubmitting) {
      return;
    }

    // TODO(BNCH-124959): add output to chat and handle errors
    setIsSubmitting(true);
    try {
      await sendDeepResearchMessage(chatId, message);
      setMessage('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classNames('sidepanel', css(styles.sidepanel))}>
      <div
        className={classNames(css(styles.sidepanelHeader), {
          [css(styles.sidepanelHeaderExpanded)]: props.isExpanded,
        })}
      >
        <ListingHeader
          isExpanded={props.isExpanded}
          crumbs={
            <SplitBreadcrumbs
              crumbs={[
                <div key="root" className={css(styles.breadcrumbsContainer)}>
                  <CrumbLink text="Deep Research" />
                  <BenchlingIcon icon="magic-wand" color={PURPLE_DARK} />
                </div>,
              ]}
            />
          }
          buttons={<ToggleDropdown className="u-mini-spacing-right" expanded={props.isExpanded} />}
          showShadow={true}
        />
        <div className={css(styles.bannerContainer)}>
          <InlineBanner
            bannerType={InlineBannerType.BETA}
            hideIcon={true}
            text={<span>This is a Benchling Intelligence Feature.</span>}
            supportingInfo={<span>This feature is powered by AI, and may make mistakes.</span>}
            action={{actionType: InlineBannerActionType.ACCORDION}}
          />
        </div>
      </div>
      <div className={css(styles.sidepanelBody)}>
        <div className={css(styles.content)}>
          <div className={css(styles.placeholder)}>
            <h3 className={css(styles.placeholderTitle)}>Get started with Benchling Deep Research</h3>
            <p className={css(styles.placeholderText)}>
              Ask questions about your Benchling data to get AI-powered insights
            </p>
          </div>
        </div>
        <div className={css(styles.inputContainer)}>
          <textarea
            className={css(styles.input)}
            placeholder="Ask a question about your Benchling data..."
            autoFocus={true}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  sidepanel: {
    display: 'flex',
    flexDirection: 'column',
  },
  sidepanelHeader: {
    padding: 0,
  },
  sidepanelHeaderExpanded: {
    padding: '16px 12px 0',
  },
  sidepanelBody: {
    padding: '4px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  content: {
    minHeight: 0,
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholder: {
    textAlign: 'center',
    maxWidth: '460px',
    width: 'calc(100% - 32px)',
  },
  placeholderTitle: {
    margin: '16px 0 8px',
    fontSize: '18px',
    color: '#333',
  },
  placeholderText: {
    margin: 0,
    padding: 0,
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#666',
  },
  inputContainer: {
    padding: '16px',
    marginTop: 'auto',
    borderTop: '1px solid #eaeaea',
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
  },
  input: {
    width: '100%',
    maxWidth: '760px',
    minWidth: 0,
    minHeight: '80px',
    maxHeight: '400px',
    padding: '12px',
    fontFamily: 'inherit',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    resize: 'none',
    ':focus': {
      outline: 'none',
      borderColor: BLUE_LIGHT,
      boxShadow: '0 0 0 2px rgba(74, 144, 226, 0.2)',
    },
  },
  bannerContainer: {
    margin: '8px',
  },
  breadcrumbsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
});

export default DeepResearchSidepanel;
